import React, { useState, useEffect, useRef } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Swal from 'sweetalert2'
import type { TenantThemeConfig } from '../types/theme'
import * as authService from '../services/authService'
import { getRuntimeTenantIdSync } from '../lib/runtimeTenant'
import { getApiBaseUrl } from '../lib/apiBaseUrl'
import {
  closeFirebaseAuthBridge,
  primeFirebaseAuthBridgeWindow,
  redirectToFirebaseAuthBridgeSend,
  redirectToFirebaseAuthBridgeVerify,
  reportFirebaseSmsBilling,
  saveLineBridgeFlowContext,
  sendOTPViaFirebaseAuthBridge,
  shouldUseFirebaseAuthBridge,
  shouldUseFirebaseAuthBridgeRedirect,
  verifyOTPViaFirebaseAuthBridge,
  clearLineBridgeFlowContext,
  loadLineBridgeFlowContext,
} from '../lib/firebaseAuthBridge'
import {
  persistModernRegisterCompleteFlowState,
  type ModernRegisterCompleteFlowState,
} from '@huay/shared/registerFlowState'
import {
  verifyRegistrationOTP,
  checkMobileAvailability,
  checkRegistrationOTPCooldown,
  recordRegistrationOTPSent,
  getSMSConfigPublic,
  normalizeThaiMobile,
  isValidThaiMobile,
  toE164Thai,
} from '../services/registrationService'
import { resolveReferralToken } from '../services/affiliateService'

/** @deprecated use reportFirebaseSmsBilling from firebaseAuthBridge */
function reportFirebaseSMSUsage(tenantId: string, e164Phone: string) {
  reportFirebaseSmsBilling({
    apiBaseUrl: getApiBaseUrl(),
    tenantId,
    phoneNumber: e164Phone,
    status: 'sent',
    messageType: 'registration',
    source: 'frontend-modern-registration',
  })
}
declare global {
  interface Window {
    phoneConfirmation?: { confirm: (code: string) => Promise<{ user: { uid: string } }> }
    recaptchaVerifier?: { clear?: () => void } | null
  }
}

/** หน้า Register (สมัครสมาชิก) — ส่ง OTP ผ่าน backend หรือ Firebase Client (เมื่อ tenant ใช้ Firebase) */
interface ModernRegisterPageProps {
  themeConfig: TenantThemeConfig | null
}

type PersistedOtpCooldown = {
  phoneNumber: string
  cooldownUntil: number
}

type PersistedOtpFlow = {
  phone: string
  otpRequested: boolean
  sessionId: string | null
  cooldownUntil: number | null
}

const AFFILIATE_REFERRAL_KEY = 'affiliate_referral_code'

const toThaiPhoneDisplay = (phoneNumber?: string) => {
  if (!phoneNumber) return ''
  const cleaned = String(phoneNumber).trim()
  if (cleaned.startsWith('+66') && cleaned.length >= 12) {
    return `0${cleaned.slice(3)}`
  }
  return cleaned
}

const getOtpCooldownStorageKey = (tenantId: string) => `huay-register-otp-cooldown:${tenantId || 'unknown'}`
const getOtpFlowStorageKey = (tenantId: string) => `huay-register-otp-flow:${tenantId || 'unknown'}`

const readPersistedOtpCooldown = (tenantId: string): PersistedOtpCooldown | null => {
  try {
    const raw = localStorage.getItem(getOtpCooldownStorageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpCooldown
    if (!parsed?.cooldownUntil || parsed.cooldownUntil <= Date.now()) {
      localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const persistOtpCooldown = (tenantId: string, data: PersistedOtpCooldown) => {
  try {
    localStorage.setItem(getOtpCooldownStorageKey(tenantId), JSON.stringify(data))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpCooldown = (tenantId: string) => {
  try {
    localStorage.removeItem(getOtpCooldownStorageKey(tenantId))
  } catch {
    // Ignore storage cleanup failures.
  }
}

const readPersistedOtpFlow = (tenantId: string): PersistedOtpFlow | null => {
  try {
    const raw = localStorage.getItem(getOtpFlowStorageKey(tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw) as PersistedOtpFlow
    if (!parsed?.phone || !parsed?.sessionId || !parsed?.otpRequested) {
      return null
    }
    if (!parsed.cooldownUntil || parsed.cooldownUntil <= Date.now()) {
      localStorage.removeItem(getOtpFlowStorageKey(tenantId))
      return null
    }
    return parsed
  } catch {
    return null
  }
}

const persistOtpFlow = (tenantId: string, data: PersistedOtpFlow) => {
  try {
    localStorage.setItem(getOtpFlowStorageKey(tenantId), JSON.stringify(data))
  } catch {
    // Ignore storage failures.
  }
}

const clearPersistedOtpFlow = (tenantId: string) => {
  try {
    localStorage.removeItem(getOtpFlowStorageKey(tenantId))
  } catch {
    // Ignore storage cleanup failures.
  }
}

const ModernRegisterPage: React.FC<ModernRegisterPageProps> = ({ themeConfig }) => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const lineBridgeHandledRef = useRef(false)
  const [phone, setPhone] = useState('')
  const [otpCode, setOtpCode] = useState('')
  const [otpRequested, setOtpRequested] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [loadingOtp, setLoadingOtp] = useState(false)
  const [loadingVerify, setLoadingVerify] = useState(false)
  const [tryFirebaseClient, setTryFirebaseClient] = useState(false)
  const [preferredBackendProvider, setPreferredBackendProvider] = useState<string | null>(null)
  const [smsConfigReady, setSmsConfigReady] = useState(false)
  /** เวลาที่กดขอ OTP ได้อีก (timestamp) — หลังกดขอ OTP แล้วต้องรอจนถึงเวลานี้ */
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
  const [, setTick] = useState(0)
  /** เหมือน user-classic: ข้อความใต้ช่องเบอร์ (เบอร์ซ้ำ / พร้อมใช้งาน / กำลังตรวจสอบ) */
  const [phoneValidationMessage, setPhoneValidationMessage] = useState('')
  const [phoneExistsInSystem, setPhoneExistsInSystem] = useState(false)
  const [isCheckingPhone, setIsCheckingPhone] = useState(false)
  const [phoneCanUseOtp, setPhoneCanUseOtp] = useState(false)

  useEffect(() => {
    const ref = searchParams.get('ref')
    const aff = searchParams.get('aff')
    if (ref && ref.trim()) {
      resolveReferralToken(ref.trim())
        .then(({ referralCode }) => {
          sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, referralCode)
        })
        .catch(() => { /* invalid/expired ref */ })
    } else if (aff && aff.trim()) {
      sessionStorage.setItem(AFFILIATE_REFERRAL_KEY, aff.trim())
    }
  }, [searchParams])

  useEffect(() => {
    if (lineBridgeHandledRef.current || !themeConfig?.tenantId) {
      return
    }

    const lineBridge = searchParams.get('lineBridge')
    if (!lineBridge) {
      return
    }

    lineBridgeHandledRef.current = true
    const tenantId = themeConfig.tenantId
    const lineFlow = loadLineBridgeFlowContext()
    const apiBaseUrl = getApiBaseUrl()

    const clearLineBridgeQuery = () => {
      const nextParams = new URLSearchParams(searchParams)
      nextParams.delete('lineBridge')
      nextParams.delete('flowId')
      nextParams.delete('firebaseUid')
      nextParams.delete('sessionId')
      nextParams.delete('errorMessage')
      setSearchParams(nextParams, { replace: true })
    }

    if (lineBridge === 'sent' && lineFlow) {
      setPhone(toThaiPhoneDisplay(lineFlow.phoneNumber))
      setSessionId(lineFlow.sessionId)
      setOtpRequested(true)
      setOtpCode('')
      setCooldownUntil(Date.now() + 5 * 60 * 1000)
      recordRegistrationOTPSent(tenantId, toThaiPhoneDisplay(lineFlow.phoneNumber)).catch(() => {})
      reportFirebaseSmsBilling({
        apiBaseUrl: lineFlow.apiBaseUrl || apiBaseUrl,
        tenantId: lineFlow.tenantId || tenantId,
        phoneNumber: lineFlow.phoneNumber,
        status: 'sent',
        messageType: 'registration',
        source: 'frontend-modern-registration-bridge-line',
      })
      clearLineBridgeQuery()
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3000,
        icon: 'success',
        title: 'ส่ง OTP ไปยังเบอร์โทรศัพท์แล้ว กรุณากรอกรหัส 6 หลัก',
      })
      return
    }

    if (lineBridge === 'verified') {
      const sessionIdFromQuery = searchParams.get('sessionId') || lineFlow?.sessionId || sessionId || ''
      const phoneFromFlow = normalizeThaiMobile(
        toThaiPhoneDisplay(lineFlow?.phoneNumber || '') || phone
      )

      clearLineBridgeFlowContext()
      clearLineBridgeQuery()

      if (!phoneFromFlow || !sessionIdFromQuery) {
        void Swal.fire({
          icon: 'error',
          title: 'ข้อมูลไม่ครบถ้วน',
          text: 'ไม่พบข้อมูลการยืนยัน OTP กรุณาเริ่มสมัครใหม่',
          confirmButtonText: 'ตกลง',
        }).then(() => {
          navigate('/register', { replace: true })
        })
        return
      }

      if (phoneFromFlow) setPhone(phoneFromFlow)
      if (sessionIdFromQuery) setSessionId(sessionIdFromQuery)

      void Swal.fire({
        title: 'ยืนยันเบอร์โทรสำเร็จ',
        text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
        icon: 'success',
        timer: 2500,
        showConfirmButton: false,
      }).then(() => {
        const completeState: ModernRegisterCompleteFlowState = {
          fromOtp: true,
          phone: phoneFromFlow,
          sessionId: sessionIdFromQuery,
        }
        persistModernRegisterCompleteFlowState(completeState)
        clearPersistedOtpFlow(tenantId)
        navigate('/register/complete', {
          replace: true,
          state: completeState,
        })
      })
      return
    }

    if (lineBridge === 'error') {
      clearLineBridgeFlowContext()
      clearLineBridgeQuery()
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 5000,
        icon: 'error',
        title: searchParams.get('errorMessage') || 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ',
      })
    }
  }, [navigate, phone, searchParams, sessionId, setSearchParams, themeConfig?.tenantId])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const persisted = readPersistedOtpCooldown(themeConfig.tenantId)
    if (!persisted) return

    setCooldownUntil(persisted.cooldownUntil)
    setPhone((prev) => prev || persisted.phoneNumber || '')
  }, [themeConfig?.tenantId])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const persisted = readPersistedOtpFlow(themeConfig.tenantId)
    if (!persisted) return

    setPhone((prev) => prev || persisted.phone || '')
    setSessionId((prev) => prev || persisted.sessionId)
    setOtpRequested(persisted.otpRequested)
    const persistedCooldownUntil = persisted.cooldownUntil
    if (persistedCooldownUntil && persistedCooldownUntil > Date.now()) {
      setCooldownUntil((prev) => prev && prev > persistedCooldownUntil ? prev : persistedCooldownUntil)
    }
  }, [themeConfig?.tenantId])


  useEffect(() => {
    if (authService.isAuthenticated()) {
      navigate('/', { replace: true })
    }
  }, [navigate])

  useEffect(() => {
    if (!themeConfig?.tenantId) return
    const tenantId = themeConfig.tenantId
    getSMSConfigPublic(tenantId)
      .then((data) => {
        setSmsConfigReady(true)
        const orderedProviders = (data?.providers || [])
          .filter((provider) => typeof provider?.type === 'string' && provider.type.trim())
          .sort((a, b) => (a.priority ?? 999) - (b.priority ?? 999))
        const smsEnabled = data?.smsEnabled === true
        const primary = orderedProviders[0]?.type ?? data?.primaryProvider ?? (smsEnabled ? 'firebase' : 'unknown')
        setPreferredBackendProvider(null)
        setTryFirebaseClient(smsEnabled && (primary === 'firebase' || orderedProviders.length === 0))
      })
      .catch(() => {
        setSmsConfigReady(true)
        setPreferredBackendProvider(null)
        setTryFirebaseClient(false)
      })
  }, [themeConfig?.tenantId])

  // เมื่อมีเบอร์ครบ 10 หลัก — เช็ค cooldown จาก backend (ดักที่ backend ไม่ให้ reload ลัด)
  useEffect(() => {
    if (!themeConfig?.tenantId || !phone) return
    const norm = normalizeThaiMobile(phone.trim())
    if (!isValidThaiMobile(norm)) {
      setPhoneCanUseOtp(false)
      return
    }
    checkRegistrationOTPCooldown(themeConfig.tenantId, norm).then((r) => {
      if (r.success && !r.allowed && r.remainingTimeMs && r.remainingTimeMs > 0) {
        setCooldownUntil(Date.now() + r.remainingTimeMs)
        setPhoneCanUseOtp(false)
        return
      }
      if (r.success && r.allowed) {
        setPhoneCanUseOtp(true)
      }
    }).catch(() => {
      setPhoneCanUseOtp(false)
    })
  }, [themeConfig?.tenantId, phone])

  useEffect(() => {
    if (!themeConfig?.tenantId) return

    if (cooldownUntil != null && cooldownUntil > Date.now()) {
      const existing = readPersistedOtpCooldown(themeConfig.tenantId)
      persistOtpCooldown(themeConfig.tenantId, {
        phoneNumber: phone || existing?.phoneNumber || '',
        cooldownUntil,
      })
      return
    }

    clearPersistedOtpCooldown(themeConfig.tenantId)
  }, [themeConfig?.tenantId, phone, cooldownUntil])

  useEffect(() => {
    if (!themeConfig?.tenantId) return

    if (otpRequested && sessionId && cooldownUntil && cooldownUntil > Date.now()) {
      persistOtpFlow(themeConfig.tenantId, {
        phone,
        otpRequested,
        sessionId,
        cooldownUntil,
      })
      return
    }

    clearPersistedOtpFlow(themeConfig.tenantId)
  }, [themeConfig?.tenantId, phone, otpRequested, sessionId, cooldownUntil])

  // 🔍 Real-time เช็คเบอร์มีในระบบหรือไม่เมื่อพิมพ์ครบ 10 หลัก (เหมือน user-classic) — แจ้ง inline + SweetAlert แล้ว redirect
  useEffect(() => {
    const tid = themeConfig?.tenantId
    if (!tid) return
    const trimmed = phone.trim().replace(/\D/g, '')
    if (trimmed.length !== 10) {
      setPhoneValidationMessage('')
      setPhoneExistsInSystem(false)
      setPhoneCanUseOtp(false)
      return
    }
    const norm = normalizeThaiMobile(trimmed)
    if (!isValidThaiMobile(norm)) {
      setPhoneValidationMessage('')
      setPhoneExistsInSystem(false)
      setPhoneCanUseOtp(false)
      return
    }
    let cancelled = false
    setIsCheckingPhone(true)
    setPhoneValidationMessage('กำลังตรวจสอบเบอร์มือถือ...')
    checkMobileAvailability(tid, norm)
      .then((res) => {
        if (cancelled) return
        if (res.success) {
          if (res.available) {
            setPhoneExistsInSystem(false)
            setPhoneValidationMessage('เบอร์นี้สามารถใช้งานได้')
          } else {
            setPhoneExistsInSystem(true)
            setPhoneCanUseOtp(false)
            setPhoneValidationMessage('เบอร์มือถือนี้มีในระบบแล้ว')
            showPhoneExistsPopup(norm)
          }
        } else {
          setPhoneExistsInSystem(false)
          setPhoneCanUseOtp(false)
          setPhoneValidationMessage('ไม่สามารถตรวจสอบเบอร์มือถือได้')
        }
      })
      .catch(() => {
        if (!cancelled) {
          setPhoneExistsInSystem(false)
          setPhoneCanUseOtp(false)
          setPhoneValidationMessage('เกิดข้อผิดพลาดในการตรวจสอบ')
        }
      })
      .finally(() => {
        if (!cancelled) setIsCheckingPhone(false)
      })
    return () => {
      cancelled = true
    }
  }, [themeConfig?.tenantId, phone])

  // อัปเดต countdown ทุก 1 วินาที เมื่ออยู่ระหว่าง cooldown
  useEffect(() => {
    if (cooldownUntil == null) return
    const remaining = cooldownUntil - Date.now()
    if (remaining <= 0) {
      setCooldownUntil(null)
      return
    }
    const t = setInterval(() => {
      setTick((n) => n + 1)
      if (Date.now() >= cooldownUntil) setCooldownUntil(null)
    }, 1000)
    return () => clearInterval(t)
  }, [cooldownUntil])

  if (!themeConfig) {
    return (
      <div className="wrap_login d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
        <div className="c_tertiary">กำลังโหลด...</div>
        <div className="bg_show" />
      </div>
    )
  }

  const tenantId = themeConfig.tenantId || getRuntimeTenantIdSync()
  const logoUrl = themeConfig.theme?.branding?.logoUrl
  /** ใช้ public SMS config เป็นตัวตัดสินหลัก เพื่อให้ bridge-only ทำงานได้แม้ tenant theme ยังไม่ sync */
  const smsOtpEnabled = smsConfigReady
    ? (tryFirebaseClient || !!preferredBackendProvider)
    : themeConfig.settings?.smsOtpEnabled === true

  /** เบอร์มีในระบบแล้ว: แจ้งเตือน + นับถอยหลัง 15 วินาที (ห้ามปิด) แล้ว redirect /login */
  const showPhoneExistsPopup = async (phoneDisplay: string) => {
    await Swal.fire({
      icon: 'warning',
      title: 'มีเบอร์นี้ในระบบแล้ว',
      html: `
        <p>เบอร์ <strong>${phoneDisplay}</strong> มีในระบบอยู่แล้ว</p>
        <p class="mb-0">หากไม่ใช่ข้อมูลคุณ กรุณาติดต่อแอดมิน</p>
        <p class="mt-2 mb-0">จะนำคุณไปยังหน้าเข้าสู่ระบบใน <span id="countdown">15</span> วินาที</p>
      `,
      allowOutsideClick: false,
      allowEscapeKey: false,
      showConfirmButton: false,
      timer: 15000,
      timerProgressBar: true,
      didOpen: () => {
        const el = document.getElementById('countdown')
        let n = 15
        const iv = setInterval(() => {
          n--
          if (el) el.textContent = String(n)
          if (n <= 0) clearInterval(iv)
        }, 1000)
      },
    })
    navigate('/login', { replace: true })
  }

  const handleRequestOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์' })
      return
    }
    if (!isValidThaiMobile(normalizeThaiMobile(trimmed))) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3500,
        icon: 'warning',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง (ใช้ได้เฉพาะ 10 หลัก ขึ้นต้น 06, 08, 09)',
      })
      return
    }
    setLoadingOtp(true)
    try {
      const bridgeEnabled = tryFirebaseClient && shouldUseFirebaseAuthBridge()
      const lineBridgeRedirect = bridgeEnabled && shouldUseFirebaseAuthBridgeRedirect()
      let bridgePrimed = false

      if (bridgeEnabled && !lineBridgeRedirect) {
        try {
          primeFirebaseAuthBridgeWindow()
          bridgePrimed = true
        } catch (primeError) {
          console.warn('⚠️ Failed to pre-open Firebase auth bridge popup:', primeError)
        }
      }

      const cooldownCheck = await checkRegistrationOTPCooldown(tenantId, trimmed)
      if (cooldownCheck.success && !cooldownCheck.allowed && (cooldownCheck.remainingTimeMs ?? 0) > 0) {
        setCooldownUntil(Date.now() + (cooldownCheck.remainingTimeMs ?? 0))
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 5000,
          icon: 'warning',
          title: `กรุณารออีก ${cooldownCheck.remainingTimeMin ?? 0} นาที จึงสามารถขอ OTP ใหม่ได้`,
        })
        if (bridgePrimed) closeFirebaseAuthBridge()
        setLoadingOtp(false)
        return
      }
      // 🔍 เช็คเบอร์มีในระบบหรือไม่ก่อนขอ OTP (เหมือน user-classic) — ถ้าซ้ำ แจ้ง SweetAlert แล้ว redirect /login
      const check = await checkMobileAvailability(tenantId, trimmed)
      if (!check.success) {
        setLoadingOtp(false)
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 4000,
          icon: 'warning',
          title: check.error || 'ไม่สามารถตรวจสอบเบอร์มือถือได้',
        })
        if (bridgePrimed) closeFirebaseAuthBridge()
        return
      }
      if (!check.available) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        setLoadingOtp(false)
        await showPhoneExistsPopup(normalizeThaiMobile(trimmed))
        return
      }
      if (!tryFirebaseClient) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error('ระบบ OTP ยังไม่พร้อม: tenant นี้ต้องใช้ Firebase ผ่าน central auth bridge เท่านั้น')
      }

      if (!bridgeEnabled) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error('Firebase auth bridge ยังไม่พร้อมสำหรับโดเมนนี้')
      }

      try {
        const e164Phone = toE164Thai(trimmed)
        const apiBaseUrl = getApiBaseUrl()

        if (lineBridgeRedirect) {
          const nextSessionId = `firebase_bridge_line_${Date.now()}`
          const flowId = crypto.randomUUID()
          saveLineBridgeFlowContext({
            flowId,
            tenantId,
            phoneNumber: e164Phone,
            apiBaseUrl,
            sessionId: nextSessionId,
            createdAt: Date.now(),
          })
          redirectToFirebaseAuthBridgeSend({
            tenantId,
            phoneNumber: e164Phone,
            apiBaseUrl,
            returnUrl: `${window.location.origin}/register`,
          }, flowId)
          return
        }

        await sendOTPViaFirebaseAuthBridge({
          tenantId,
          phoneNumber: e164Phone,
          apiBaseUrl,
        })
        const sid = 'firebase_bridge_' + Date.now()
        setSessionId(sid)
        setOtpRequested(true)
        setOtpCode('')
        reportFirebaseSMSUsage(tenantId, e164Phone)
        recordRegistrationOTPSent(tenantId, trimmed).catch(() => {})
        const waitMs = 5 * 60 * 1000
        setCooldownUntil(Date.now() + waitMs)
        Swal.fire({
          toast: true,
          position: 'top',
          timer: 3000,
          icon: 'success',
          title: 'ส่ง OTP ไปยังเบอร์โทรศัพท์แล้ว กรุณากรอกรหัส 6 หลัก',
        })
        setLoadingOtp(false)
        return
      } catch (bridgeError) {
        if (bridgePrimed) closeFirebaseAuthBridge()
        throw new Error(
          bridgeError instanceof Error
            ? bridgeError.message
            : 'Firebase auth bridge ส่ง OTP ไม่สำเร็จ'
        )
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } }; message?: string }
      const msg = err.response?.data?.message || err.message || 'เกิดข้อผิดพลาด กรุณาลองใหม่'
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 4000,
        icon: 'error',
        title: msg,
      })
    } finally {
      setLoadingOtp(false)
    }
  }

  /** เมื่อปิด OTP: กด "ถัดไป" → เช็คเบอร์มีในระบบหรือไม่ ถ้ามี แจ้งเตือน 15 วินาที แล้วไป /login */
  const handleNextWithoutOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์' })
      return
    }
    if (!isValidThaiMobile(normalizeThaiMobile(trimmed))) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3500,
        icon: 'warning',
        title: 'เบอร์โทรศัพท์ไม่ถูกต้อง (ใช้ได้เฉพาะ 10 หลัก ขึ้นต้น 06, 08, 09)',
      })
      return
    }
    const check = await checkMobileAvailability(tenantId, trimmed)
    if (check.success && !check.available) {
      await showPhoneExistsPopup(normalizeThaiMobile(trimmed))
      return
    }
    navigate('/login', { replace: true })
  }

  const handleVerifyOtp = async () => {
    const trimmed = phone.trim()
    if (!trimmed) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณาใส่เบอร์โทรศัพท์และขอ OTP ก่อน' })
      return
    }
    if (!otpRequested || !sessionId) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณากด ขอ OTP ก่อน' })
      return
    }
    if (!/^\d{6}$/.test(otpCode)) {
      Swal.fire({ toast: true, position: 'top', timer: 3000, icon: 'warning', title: 'กรุณากรอกรหัส OTP 6 หลัก' })
      return
    }
    setLoadingVerify(true)
    try {
      if (sessionId.startsWith('firebase_bridge_line_')) {
        const lineFlow = loadLineBridgeFlowContext()
        if (!lineFlow) {
          throw new Error('ไม่พบ session สำหรับยืนยัน OTP กรุณาเริ่มสมัครใหม่')
        }

        redirectToFirebaseAuthBridgeVerify({
          flowId: lineFlow.flowId,
          otpCode,
          returnUrl: `${window.location.origin}/register`,
        })
        return
      }

      if (sessionId.startsWith('firebase_bridge_')) {
        await verifyOTPViaFirebaseAuthBridge(otpCode)
        closeFirebaseAuthBridge()
        Swal.fire({
          title: 'ยืนยันเบอร์โทรสำเร็จ',
          text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        }).then(() => goToComplete())
      } else if (sessionId.startsWith('firebase_') && window.phoneConfirmation) {
        await window.phoneConfirmation.confirm(otpCode)
        window.phoneConfirmation = undefined
        Swal.fire({
          title: 'ยืนยันเบอร์โทรสำเร็จ',
          text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
          icon: 'success',
          timer: 2500,
          showConfirmButton: false,
        }).then(() => goToComplete())
      } else {
        const result = await verifyRegistrationOTP(tenantId, trimmed, otpCode, sessionId)
        if (result.success) {
          Swal.fire({
            title: 'ยืนยันเบอร์โทรสำเร็จ',
            text: 'กรุณากรอกรหัสผ่านและข้อมูลบัญชีในหน้าถัดไป',
            icon: 'success',
            timer: 2500,
            showConfirmButton: false,
          }).then(() => goToComplete())
        } else {
          Swal.fire({
            toast: true,
            position: 'top',
            timer: 4000,
            icon: 'error',
            title: result.message || 'ยืนยัน OTP ไม่สำเร็จ',
          })
        }
      }
    } catch (e) {
      Swal.fire({
        toast: true,
        position: 'top',
        timer: 3000,
        icon: 'error',
        title: 'เกิดข้อผิดพลาด กรุณาลองใหม่',
      })
    } finally {
      setLoadingVerify(false)
    }
  }

  const goToComplete = () => {
    const trimmed = phone.trim()
    if (!trimmed || !sessionId) return

    const completeState: ModernRegisterCompleteFlowState = {
      fromOtp: true,
      phone: normalizeThaiMobile(trimmed),
      sessionId,
    }
    persistModernRegisterCompleteFlowState(completeState)
    clearPersistedOtpFlow(tenantId)
    navigate('/register/complete', {
      replace: true,
      state: completeState,
    })
  }

  return (
    <div className="wrap_login">
      <div className="container">
        <div className="">
          <div className="img_logo_l mb-3 mb-md-5">
            {logoUrl ? (
              <img src={logoUrl} alt={themeConfig.theme?.branding?.siteName || ''} />
            ) : (
              <span className="c_tertiary font_md">Logo</span>
            )}
          </div>

          <div className="c_tertiary text-center my-3">
            <h3 className="">สมัครสมาชิก</h3>
            <p className="">ลงทะเบียน — กรอกเบอร์โทรศัพท์และยืนยัน OTP</p>
          </div>

          <>
          {themeConfig.settings?.lineLoginEnabled !== false && (
            <>
              <a
                href="#"
                className="link_pmr box_row justify-content-center font_rg bg_gd_quaternary border_10 ins_sd text-decoration-none"
                onClick={(e) => e.preventDefault()}
              >
                <span className="c_tertiary d-flex align-items-center">
                  <i className="fab fa-line fs-3 me-2"></i>
                  ลงชื่อเข้าใช้ด้วย LINE
                </span>
              </a>
              <h6 className="c_tertiary text-center my-3">หรือ</h6>
            </>
          )}

          <div className="form_inp">
            <div className="input_box border_10 mb-2 position-relative">
              <div className="icon">
                <i className="fas fa-mobile-alt c_secondary"></i>
              </div>
              <input
                type="tel"
                inputMode="numeric"
                placeholder="เบอร์โทรศัพท์ (06, 08, 09)"
                value={phone}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 10)
                  setPhone(value)
                }}
                className="c_primary"
                style={{ color: '#1a1a1a' }}
                disabled={loadingOtp}
                maxLength={10}
              />
              {phoneCanUseOtp && !phoneExistsInSystem && !isCheckingPhone && (
                <div
                  className="position-absolute d-flex align-items-center"
                  style={{ right: smsOtpEnabled ? '98px' : '16px', top: '50%', transform: 'translateY(-50%)', zIndex: 2 }}
                >
                  <i className="fas fa-check-circle text-success" aria-hidden="true" />
                </div>
              )}
              {smsOtpEnabled && (() => {
                const remainingMs = cooldownUntil != null ? Math.max(0, cooldownUntil - Date.now()) : 0
                const inCooldown = remainingMs > 0
                return (
                  <button
                    type="button"
                    className="otp_link bg_gd_secondary c_tertiary border-0 px-2 px-md-3 font_md"
                    onClick={handleRequestOtp}
                    disabled={loadingOtp || inCooldown}
                  >
                    {loadingOtp ? 'กำลังส่ง...' : inCooldown ? 'ขอ OTP ใหม่' : 'ขอ OTP'}
                  </button>
                )
              })()}
            </div>
            {phoneValidationMessage && (
              <p
                className={`small mb-0 mt-1 ${phoneExistsInSystem ? 'text-danger' : isCheckingPhone ? 'text-muted' : 'text-success'}`}
                style={{ fontSize: '0.875rem' }}
              >
                {phoneExistsInSystem && '❌ '}
                {phoneValidationMessage}
              </p>
            )}
          </div>

          {smsOtpEnabled && cooldownUntil != null && Date.now() < cooldownUntil && (
            <div className="mb-2 text-center">
              <p className="small mb-2 text-warning">
                {(() => {
                  const remainingMs = Math.max(0, cooldownUntil - Date.now())
                  const min = Math.floor(remainingMs / 60000)
                  const sec = Math.floor((remainingMs % 60000) / 1000)
                  return min > 0
                    ? `กรุณารออีก ${min} นาที ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                    : `กรุณารออีก ${sec} วินาที จึงสามารถขอ OTP ใหม่ได้`
                })()}
              </p>
              {otpRequested ? (
                <p className="small mb-0 text-info">คุณยังสามารถใช้ OTP เดิมที่ได้รับเพื่อยืนยันต่อด้านล่างได้</p>
              ) : null}
            </div>
          )}

          {!smsOtpEnabled && (
            <p className="c_tertiary text-center small mb-2 d-flex align-items-center justify-content-center gap-1">
              <span className="text-success">✔</span>
              ไม่ต้องยืนยัน SMS สำหรับการสมัครสมาชิก
            </p>
          )}

          {smsOtpEnabled && otpRequested && (
            <div className="form_inp mb-2">
              <div className="input_box border_10 position-relative">
                <div className="icon">
                  <i className="fas fa-key c_secondary"></i>
                </div>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="รหัส OTP 6 หลัก"
                  value={otpCode}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setOtpCode(v)
                  }}
                  className="c_primary"
                  style={{ color: '#1a1a1a' }}
                  disabled={loadingVerify}
                  maxLength={6}
                />
              </div>
            </div>
          )}

          {smsOtpEnabled ? (
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd w-100 border-0 text-decoration-none"
              onClick={handleVerifyOtp}
              disabled={loadingVerify || !otpRequested}
            >
              <span className="c_tertiary">
                {loadingVerify ? 'กำลังยืนยัน...' : 'ส่ง OTP'}
              </span>
            </button>
          ) : (
            <button
              type="button"
              className="link_pmr box_row justify-content-center font_rg bg_primary border_10 ins_sd w-100 border-0 text-decoration-none"
              onClick={handleNextWithoutOtp}
            >
              <span className="c_tertiary">ถัดไป →</span>
            </button>
          )}

          <h6 className="c_tertiary text-center my-3">คุณมีบัญชีอยู่แล้วใช่หรือไม่?</h6>

          <Link
            to="/login"
            className="link_pmr box_row justify-content-center font_rg bg_gd_tertiary border_10 ins_sd text-decoration-none"
          >
            <div className="d-flex align-items-center ft_sd c_tertiary font_md">
              <span className="">เข้าสู่ระบบ</span>
            </div>
          </Link>
          </>
        </div>
      </div>

      <div className="bg_show" />
    </div>
  )
}

export default ModernRegisterPage
